import { useMutation } from "@tanstack/react-query"
import { useTheme } from "next-themes"
import { useEffect } from "react"

import useIsDesktop from "@hooks/useIsDesktop"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { TabItem } from "@atoms"

import { ListItemStack, TabStack } from "@molecules"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import toast from "@molecules/Toast/Toast"

import { colorSchemes } from "@constants/colorSchemes"
import { themeNameBySchemeCode } from "@constants/mappings/themeNameBySchemeCode.mapping"

import { patchColorMode } from "./AppAppearanceSelector.utils"

export default function AppAppearanceSelector() {
    const { theme, setTheme } = useTheme()

    const { user } = useUser()

    const {
        mutate: mutateColorMode,
        isError,
        isSuccess,
    } = useMutation<User, Error, User["color_mode"]>({
        mutationFn: patchColorMode,
    })

    useEffect(() => {
        if (isError) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not update appearance setting",
            })

            // Reset to original configuration
            if (user) {
                setTheme(themeNameBySchemeCode[user.color_mode])
            }
        }
        if (isSuccess) {
            toast({
                type: "success",
                variant: "default",
                title: "Appearance setting updated",
            })
        }
    }, [isError, isSuccess])

    const handleColorSchemePick = (colorScheme: User["color_mode"]) => {
        setTheme(themeNameBySchemeCode[colorScheme])
        mutateColorMode(colorScheme)
    }

    const isDesktop = useIsDesktop()
    const themeTabs = (
        <TabStack variant="contained" size="lg" isFullWidth={!isDesktop}>
            <TabItem
                icon={faIcon({ name: "desktop", style: "regular" })}
                iconActive={faIcon({ name: "desktop", style: "solid" })}
                isSelected={theme === "system"}
                onClick={() => handleColorSchemePick(colorSchemes.default)}
                noDebounce={true}
            >
                System
            </TabItem>
            <TabItem
                icon={faIcon({ name: "sun-bright", style: "regular" })}
                iconActive={faIcon({ name: "sun-bright", style: "solid" })}
                isSelected={theme === "light"}
                onClick={() => handleColorSchemePick(colorSchemes.light)}
                noDebounce={true}
            >
                Light
            </TabItem>
            <TabItem
                icon={faIcon({ name: "moon", style: "regular" })}
                iconActive={faIcon({ name: "moon", style: "solid" })}
                isSelected={theme === "dark"}
                onClick={() => handleColorSchemePick(colorSchemes.dark)}
                noDebounce={true}
            >
                Dark
            </TabItem>
        </TabStack>
    )

    return (
        <ListItemStack>
            <ListItem>
                <ListItem.AvatarAndContent
                    content={<ListItemContent leftLabel={<ListItem.Label label="Theme" />} />}
                />
                {isDesktop ? (
                    <ListItem.Actions>{themeTabs}</ListItem.Actions>
                ) : (
                    <ListItem.Bottom>{themeTabs}</ListItem.Bottom>
                )}
            </ListItem>
        </ListItemStack>
    )
}
